import React, { useState, useEffect } from "react";
import { useDataProvider, useNotify, useRefresh, AutocompleteInput, SimpleFormIterator, useSimpleFormIteratorItem, ArrayInput, TextInput, ReferenceInput, required } from "react-admin";
import { Box } from "@mui/system";
import { Grid, Typography, Button } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import VacationRequestPeriod from "./VacationRequestPeriod";
import VacationRequestPreview from "./VacationRequestPreview";
import { settings } from "app";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import { checkPermission } from "server/security/utils";
import { getPermissions } from "auth/security/securityUtils";
import { VACATION_FILES_LIMIT } from "utils/constants";
import { getDisplayName } from "utils/helpers";
import { useNotifyError } from "utils/useNotifyError";
import { DocumentEditRowIteratorItem } from "components/Leaves/components/DocumentEditRow";
import FileInputWithlabel from "components/Leaves/components/FileInputWithLabel";
import { employeeRequests } from "api/employeeApi";
import RemainingVacationDaysTable from "./RemainingVacationDaysTable";
import ContractsSelection, { CONTRACTS_SOURCE, getContractIds } from "./ContractsSelection";
import { useEmployeeContracts } from "../../utils/useEmployeeContracts";
import ResetButton from "../ResetButton";

const VacationCreateDocumentRow = ({ requiredDocuments = [] }) => {
  const { index } = useSimpleFormIteratorItem();
  const documentValue = useWatch({ name: `documents.${index}.document` });

  return (
    <Grid container spacing={1}>
      {requiredDocuments?.length === 0 && (
        <Grid item xs={12}>
          <FileInputWithlabel isEnableValidate={false} />
          <TextInput
            label={"Тип на документа"}
            source="description"
            validate={documentValue ? required() : undefined}
          />
        </Grid>
      )}
      {requiredDocuments.map((docType, docIndex) => (
        <Grid item xs={12} key={docType?.id ?? docIndex}>
          <FileInputWithlabel isEnableValidate={true} label={docType?.name} />
          <TextInput
            label={"Тип на документа"}
            source="description"
            validate={documentValue ? required() : undefined}
          />
        </Grid>
      ))}
    </Grid>
  );
};

const buildRequestBody = (
  { periods, employeeId, substituteEmployeeId, contracts, contractIds, ...rest },
  canRequestForAll,
) => {
  const periodsPayload = periods?.map(({ numberOfDays, substituteName, ...period }) => period);

  return {
    ...rest,
    ...(canRequestForAll && employeeId ? { employeeId } : {}),
    // Omitted entirely when the contracts step does not apply (non-Su tenant, no contracts, failed fetch).
    ...(contractIds?.length ? { contractIds } : {}),
    periods: periodsPayload,
    documents: rest.documents?.map(({ document, ...doc }) => ({
      ...doc,
      document: document?.rawFile,
    })),
  };
};

const VacationRequest = ({ showPreview, setShowPreview, isCreate, onEditConfirm, onDocumentDelete, setLeaveId }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const notifyError = useNotifyError();
  const refresh = useRefresh();
  const navigate = useNavigate();

  const [isFormValid, setIsFormValid] = useState(false);
  const { getValues, reset, trigger, setValue } = useFormContext();
  const [previewData, setPreviewData] = useState(null);
  const documents = useWatch({ name: 'documents' });
  const employeeId = useWatch({ name: "employeeId" });
  const reasonId = useWatch({ name: "reasonId" });
  const contractIds = useWatch({ name: CONTRACTS_SOURCE });
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const { contracts, isLoading: contractsLoading, hasError: contractsError } = useEmployeeContracts(employeeId);
  // No contracts to pick from (non-Su tenant, none returned, failed fetch) — the step does not apply.
  const hasContractSelection = !contracts.length || contractIds?.length > 0;
  const canRequestForAll = checkPermission(
    getPermissions(),
    permissions.canRequestLeavesForAllEmployees,
  );

  useEffect(() => {
    if (!reasonId) {
      setRequiredDocuments([]);
      return;
    }
    let cancelled = false;
    dataProvider
      .customRequest(employeeRequests.getDocumentTypes(reasonId), null, "GET")
      .then(({ data }) => { if (!cancelled) setRequiredDocuments(data ?? []); })
      .catch(() => { if (!cancelled) setRequiredDocuments([]); });
    return () => { cancelled = true; };
  }, [reasonId, dataProvider]);

  // The substitutes endpoint is period-scoped, so batch the lookup per distinct date range.
  const attachSubstituteNames = async periods => {
    const ranges = new Map();
    (periods ?? []).forEach(period => {
      const { substituteEmployeeId, dateFrom, dateTo } = period ?? {};
      if (!substituteEmployeeId || !dateFrom || !dateTo) return;
      const key = `${dateFrom}|${dateTo}`;
      const range = ranges.get(key) ?? { dateFrom, dateTo, ids: new Set() };
      range.ids.add(substituteEmployeeId);
      ranges.set(key, range);
    });
    if (!ranges.size) return periods;

    let hasError = false;
    const substitutesByRange = new Map();
    await Promise.all(
      [...ranges].map(async ([key, { dateFrom, dateTo, ids }]) => {
        try {
          const { data } = await dataProvider.customRequest(
            employeeRequests.getSubstitutionsByIdsSelect(dateFrom, dateTo, [...ids]),
            null,
            "GET"
          );
          substitutesByRange.set(key, data ?? []);
        } catch (err) {
          console.error(err);
          hasError = true;
        }
      })
    );
    if (hasError) notify("Имената на заместниците не можаха да бъдат заредени.", { type: "error" });

    return periods.map(period => {
      const substitutes = substitutesByRange.get(`${period?.dateFrom}|${period?.dateTo}`);
      // An unfetched or failed range must keep the period as it is, not blank out its substitute.
      if (!period?.substituteEmployeeId || !substitutes) return period;
      return {
        ...period,
        substituteName:
          substitutes.find(s => s.employeeId === period.substituteEmployeeId)?.nameBg ?? null,
      };
    });
  };

  const handlePreview = async () => {
    const isValid = await trigger();
    if (!isValid) return;
    const formData = getValues();
    const periods = await attachSubstituteNames(formData.periods);
    const selectedContracts = contracts.filter(c => formData[CONTRACTS_SOURCE]?.includes(c.contractNumber));
    setPreviewData({
      ...formData,
      periods,
      contracts: selectedContracts,
      comment: formData.comment || "Няма",
    });
    setShowPreview(true);
  };

  const handleSuccessResponse = () => {
    notify("Заявката беше регистрирана! Ще получите имейл нотификация при одобрението ѝ.", { type: "success" });
    refresh();
    setShowPreview(false);
    setPreviewData(null);
    reset();
    // reset() wipes the selection, and the contracts themselves did not change — re-select them all.
    setValue(CONTRACTS_SOURCE, getContractIds(contracts));
  };

  const handleConfirm = async () => {
    try {
      await dataProvider.customRequest(
        employeeRequests.createLeave(),
        buildRequestBody(previewData, canRequestForAll),
        "POST",
        null,
        true
      );

      handleSuccessResponse();
    } catch (e) {
      notifyError(e);
    }
  };

  const handleEditConfirm = () => onEditConfirm(buildRequestBody(previewData, canRequestForAll));

  return showPreview ? (
    <VacationRequestPreview
      data={previewData}
      onBack={() => setShowPreview(false)}
      onConfirm={isCreate ? handleConfirm : handleEditConfirm}
      isCreate={isCreate}
    />
  ) : (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%", mt: 2 }}>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          md={8}
          lg={4}
        >
          {!isCreate && (
            <Typography
              variant="h6"
              sx={{ mb: 2 }}
            >
              Редакция на отпуск
            </Typography>
          )}
          <SecuredComponent permission={permissions.canRequestLeavesForAllEmployees}>
            <ReferenceInput
              source="employeeId"
              reference={employeeRequests.getEmployeesSelect()}
              filter={{ filterByUserNode: true }}
            >
              <AutocompleteInput
                sx={{ m: 0 }}
                label="Служител"
                optionValue="id"
                optionText={getDisplayName}
                readOnly={!isCreate}
                filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                matchSuggestion={() => true}
              />
            </ReferenceInput>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: -3, mb: 3 }}
            >
              попълва се в случай, че се пуска заявка за друг потребител
            </Typography>
          </SecuredComponent>
          <ReferenceInput
            key={`reason-${employeeId ?? "self"}`}
            source="reasonId"
            reference={employeeRequests.getLeaveReasonsSelect()}
            required={true}
            perPage={100}
            filter={employeeId ? { employeeId } : {}}
          >
            <AutocompleteInput
              sx={{ m: 0 }}
              label="Основание"
              validate={required()}
              optionText={record => record.note ? record.note : record.name}
              inputText={record => record.note ? record.note : record.name}
              optionValue="id"
              filterToQuery={q => (q?.length > 2 ? { search: q } : {})}
            />
          </ReferenceInput>
          <TextInput
            sx={{ m: 0 }}
            type="textarea"
            label="Коментар"
            source="comment"
            defaultValue=""
            multiline
            minRows={3}
          />
          <RemainingVacationDaysTable employeeId={employeeId} />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          lg={4}>
          <VacationRequestPeriod
            setIsFormValid={setIsFormValid}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          {!reasonId ? null : isCreate ? (
            <ArrayInput source="documents" label=" " defaultValue={[{}]}>
              <SimpleFormIterator
                disableAdd={documents?.length >= VACATION_FILES_LIMIT}
                sx={settings.style.iteratorWithGap}
              >
                <VacationCreateDocumentRow requiredDocuments={requiredDocuments} />
              </SimpleFormIterator>
            </ArrayInput>
          ) : (
            <ArrayInput source="documents" label="Документи">
              <SimpleFormIterator
                disableAdd={documents?.length >= VACATION_FILES_LIMIT}
                sx={settings.style.iteratorWithGap}
              >
                <DocumentEditRowIteratorItem
                  onDocumentDelete={onDocumentDelete}
                  requiredDocuments={requiredDocuments}
                />
              </SimpleFormIterator>
            </ArrayInput>
          )}
        </Grid>
        <Grid item xs={12}>
          <ContractsSelection
            contracts={contracts}
            isLoading={contractsLoading}
            hasError={contractsError}
          />
        </Grid>
        <Grid container
          justifyContent="space-between"
          p={0.5}
        >
          <Grid item>
            <ResetButton
              onReset={() => (isCreate ? setLeaveId?.("") : navigate(-1))}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="success"
              type="button"
              onClick={handlePreview}
              disabled={!isFormValid || !hasContractSelection}>
              Преглед на заявка
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VacationRequest;
